import { useI18n } from 'vue-i18n'
import { SECTIONS } from '@/types/sections'
import { isNodeMimeTypeGeneric } from '@deepcloud/deep-ui-utils'
import i18n from '@/plugins/i18n'
import type { BoxBreadcrumb } from '@/types/breadcrumbs.ts'
import type {
  Box,
  BoxEntry,
  BoxInfo,
  PathSegment,
} from '@deepcloud/api-deep-box'
import type { RouteLocationRaw } from 'vue-router'

const pathSegmentsKey: InjectionKey<Ref<PathSegment[] | undefined>> = Symbol(
  'BOX_PATH_SEGMENTS_KEY',
)

const currentBoxKey: InjectionKey<
  MaybeRefOrGetter<Box | BoxInfo | BoxEntry | undefined>
> = Symbol('CURRENT_BOX_KEY')

export function createBoxBreadcrumbsContext(
  pathSegments: Ref<PathSegment[] | undefined>,
  currentBox: MaybeRefOrGetter<Box | BoxInfo | BoxEntry | undefined>,
) {
  provide(pathSegmentsKey, pathSegments)
  provide(currentBoxKey, currentBox)
}

export function useBoxBreadcrumbs() {
  const pathSegments = inject(pathSegmentsKey)
  if (!pathSegments) {
    throw new Error(
      'Please call `createBoxBreadcrumbsContext` on the appropriate parent component',
    )
  }

  const { t } = useI18n()

  const boxBreadcrumbs = computed(() => {
    const breadcrumbs: BoxBreadcrumb[] = []

    toValue(pathSegments)?.forEach((item) => {
      let title = item.displayName
      let to: RouteLocationRaw | string = item.nodeId
      const disabled = false
      switch (item.name) {
        case SECTIONS.FILES:
          title = i18n.global.t('box_details.navigation.files')
          to = {
            name: 'organization-types-type-boxes-box-files',
          }
          break
        case SECTIONS.TRASH:
          title = i18n.global.t('box_details.navigation.trash')
          to = {
            name: 'organization-types-type-boxes-box-trash',
          }
          break
        case SECTIONS.INBOX:
        case SECTIONS.QUEUE:
          title = i18n.global.t('box_details.navigation.inbox')
          // Won't be ever used as we cannot go deeper in the inbox
          to = 'inbox'
          break

        // no default
      }

      // Generics
      if (isNodeMimeTypeGeneric(item.mimeType)) {
        // check for "name" instead "id" as usual for "generics" because the PathSegment do not have the "id"
        if (item.name === 'DeepSign') {
          title = t('box.private.generics.deepsign.title')
        }
      }

      breadcrumbs.push({
        exact: true,
        title,
        to,
        disabled,
        item,
      })
    })
    // Disable last item
    if (breadcrumbs.length > 0) {
      breadcrumbs[breadcrumbs.length - 1].disabled = true
    }

    return breadcrumbs
  })

  function getFilterPathSegments(
    items: BoxBreadcrumb[],
    box: Box | BoxInfo | BoxEntry,
  ) {
    if (!box || box.boxVariant !== 'private') return items
    return items.filter((s) => {
      return s.item?.name !== SECTIONS.FILES && box.boxVariant === 'private'
    })
  }

  const currentBox = inject(currentBoxKey)

  // global boxBreadcrumbs by removing the root node if name === 'files'
  const boxBreadcrumbsFiltered = computed(() => {
    const currentBoxValue = toValue(currentBox)
    if (!currentBoxValue) {
      return boxBreadcrumbs.value
    }
    return getFilterPathSegments(boxBreadcrumbs.value, currentBoxValue)
  })

  return {
    pathSegments: toRef(pathSegments),
    boxBreadcrumbs,
    boxBreadcrumbsFiltered,
  }
}
